import { Sidebar } from 'primereact/sidebar';
import { useQuery } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessage from '../../../../components/messages/ErrorMessage';
import { useRef, useState } from 'react';
import { fetchClaim } from '../../../../services/Insurance/Claim';
import {
  Claim,
  ClaimForm,
  ClaimPartyInvolved,
  ClaimStatus,
} from '../../../../Interfaces/Insurance/Claim';
import { Button } from 'primereact/button';
import { FormProvider, useForm } from 'react-hook-form';
import UpdateClaim from './UpdateClaim';
import ClaimData from './ClaimData';
import ClaimDrilldownReport from '../report/ClaimDrilldownReport';
import { useRolesAccessContext } from '@/src/app/context/RolesAccessContext';
import { useCompanyContext } from '@/src/app/context/CompanyContext';
import { ClaimFiltersOptions } from '../ClaimFilters';

type ClaimDrilldownProps = {
  visible: boolean;
  setVisible: (value: boolean) => void;
  claimID: string;
  baseUpdateStatus: ClaimFiltersOptions['claimStatus'];
  disableEdit?: boolean;
};

const ClaimDrilldown = ({
  visible,
  setVisible,
  claimID,
  baseUpdateStatus,
  disableEdit,
}: ClaimDrilldownProps) => {
  const { selectedCompany } = useCompanyContext();
  const { rolesAcess } = useRolesAccessContext();
  const [claim, setClaim] = useState<Claim>();
  const [hideUpdate, setHideUpdate] = useState(false);
  const drilldownRef = useRef<HTMLDivElement | null>(null);
  const formMethods = useForm<ClaimForm>();
  const { setValue, reset } = formMethods;
  const [showUpdateView, setShowUpdateView] = useState(false);
  const access = rolesAcess?.find((access) => access.report === 'claim');

  const { isFetching, isError, isSuccess } = useQuery({
    queryKey: ['getClaim', claimID],
    queryFn: ({ signal }) => fetchClaim(claimID, signal),
    onSuccess: (data) => {
      setClaim(data);
    },
    refetchOnWindowFocus: false,
  });

  const onUpdateShow = () => {
    if (claim) {
      setValue('policyID', claim.policy.id);
      setValue('type', claim.type);

      if (claim.employee) {
        setValue('relation', ClaimPartyInvolved.Employee);
        setValue('employeeID', claim.employee.id);
      }

      if (claim.subcontractor) {
        setValue('relation', ClaimPartyInvolved.Subcontractor);
        setValue('subcontractorID', claim.subcontractor.id);
      }

      if (claim.other) {
        setValue('relation', ClaimPartyInvolved.Other);
        setValue('other', claim.other);
      }

      if (claim.accidentDate) {
        setValue('accidentDate', new Date(claim.accidentDate));
      }

      setValue('number', claim.number);
      setValue('contacts', claim.contacts ?? []);
      setValue('status', claim.status);
      setValue('accidentDescription', claim.accidentDescription);
      setValue(
        'logs',
        claim.logs
          ?.sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          )
          .map((log) => {
            return {
              ...log,
              date: new Date(log.date),
              prevAttachment: log.attachmentName,
              attachmentPath: undefined,
              attachmentName: undefined,
            };
          }) ?? []
      );
    }
  };

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={() => {
        setVisible(false);
        setShowUpdateView(false);
        reset();
        setHideUpdate(false);
      }}
      className="w-full max-w-600px"
    >
      {isFetching && (
        <div className="mx-8 h-full flex align-items-center">
          <ProgressSpinner />
        </div>
      )}
      {isError && !isFetching && !claim && (
        <div className="h-full flex align-items-center">
          <ErrorMessage
            content={'Failed to obtain data! Please try again later.'}
          />
        </div>
      )}
      {isSuccess && !isFetching && claim && (
        <div
          id="claim-drilldown"
          ref={drilldownRef}
          className="blackText text-standard printBackground drilldownContent"
        >
          <div className="text-30px font-bold text-center mt-5 printMedia printShow">
            {selectedCompany!.name}
          </div>
          <div className="mt-2 flex flex-column sm:row-gap-2 sm:flex-row flex-wrap align-items-center justify-content-center sm:justify-content-end">
            <div className="text-28px font-bold mx-auto sm:ml-4 drilldownHeader">
              Claim Drilldown
            </div>
            {!showUpdateView && (
              <div className="text-20px sm:mr-3 mt-2 sm:mt-0 cursor-pointer printHide">
                <ClaimDrilldownReport elementRef={drilldownRef} />
              </div>
            )}
            {access?.editable &&
              selectedCompany?.isActive &&
              !disableEdit &&
              !hideUpdate && (
                <Button
                  onClick={() => {
                    if (showUpdateView) {
                      setShowUpdateView(false);
                    } else {
                      onUpdateShow();
                      setShowUpdateView(true);
                    }
                  }}
                  className={`w-8rem ${
                    showUpdateView ? 'bluwaiRed' : 'buttonBackground'
                  } border-0 flex py-3 printHide`}
                  type="button"
                >
                  <span className="mx-auto text-20px text-white">
                    {showUpdateView ? 'Close' : 'Update'}
                  </span>
                </Button>
              )}
          </div>
          <div className="mt-5 ml-4">
            {showUpdateView ? (
              <FormProvider {...formMethods}>
                <UpdateClaim
                  id={claimID}
                  afterUpdate={(claim) => setClaim(claim)}
                  filterStatus={baseUpdateStatus}
                  prevAttachment={claim.attachmentName}
                />
              </FormProvider>
            ) : (
              <ClaimData
                claim={claim}
                afterRemoval={() => setHideUpdate(true)}
                disableEdit={disableEdit}
              />
            )}
          </div>
        </div>
      )}
    </Sidebar>
  );
};

export default ClaimDrilldown;
